import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'

export interface AssistantKB {
  id: string
  name: string
  description?: string
  document_count?: number
}

export interface Assistant {
  id: string
  shared_id: string
  name: string
  description: string
  knowledge_bases: AssistantKB[]
  created_at: string
  updated_at: string
}

export interface AssistantDetail extends Assistant {
  temperature: number
  top_p: number
  presence_penalty: number
  frequency_penalty: number
  system_prompt: string
  opener: string
  empty_response: string
  similarity_threshold: number
  keywords_similarity_weight: number
  top_n: number
  show_quote: boolean
  ragflow_chat_id: string
  shared_id: string
  // Crawler settings
  enable_crawler: boolean
  crawler_include_domains: string
  crawler_exclude_domains: string
  // Intent Classification - KB Routing Examples
  kb_routing_examples: string
  // Memory
  memory_enabled: boolean
  memory_extraction_prompt: string
  memory_fact_schema: Array<{ category: string; key: string; description?: string }>
}

export interface PaginatedAssistantsResponse {
  items: Assistant[]
  total: number
  page: number
  page_size: number
}

export function useAssistants(params?: { page?: number; pageSize?: number; search?: string }) {
  const { page = 1, pageSize = 10, search = '' } = params || {}
  return useQuery<PaginatedAssistantsResponse>({
    queryKey: ['assistants', page, pageSize, search],
    queryFn: async () => {
      const response = await knowledgeApi.get('/knowledge/api/assistants/list/', {
        params: { page, page_size: pageSize, search },
      })
      return response.data
    },
  })
}

export function useBulkDeleteAssistants() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: string[]) => {
      const response = await knowledgeApi.post('/knowledge/api/assistants/bulk-delete/', { ids })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants'] })
    },
  })
}

export function useAssistant(assistantId: string | undefined) {
  return useQuery({
    queryKey: ['assistant', assistantId],
    queryFn: async () => {
      const response = await knowledgeApi.get(`/knowledge/api/assistants/${assistantId}/`)
      return response.data as AssistantDetail
    },
    enabled: !!assistantId,
  })
}

export function useCreateAssistant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      name: string
      description?: string
      knowledge_base_ids: string[]
      temperature?: number
      top_p?: number
      presence_penalty?: number
      frequency_penalty?: number
      system_prompt?: string
      opener?: string
      empty_response?: string
      similarity_threshold?: number
      keywords_similarity_weight?: number
      top_n?: number
      show_quote?: boolean
    }) => {
      const response = await knowledgeApi.post('/knowledge/api/assistants/create/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants'] })
    },
  })
}

export function useUpdateAssistant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ assistantId, data }: { 
      assistantId: string
      data: Partial<{
        name: string
        description: string
        temperature: number
        top_p: number
        presence_penalty: number
        frequency_penalty: number
        system_prompt: string
        opener: string
        empty_response: string
        similarity_threshold: number
        keywords_similarity_weight: number
        top_n: number
        show_quote: boolean
        enable_crawler: boolean
        crawler_include_domains: string
        crawler_exclude_domains: string
        kb_routing_examples: string
        memory_enabled: boolean
        memory_extraction_prompt: string
        memory_fact_schema: Array<{ category: string; key: string; description?: string }>
      }>
    }) => {
      const response = await knowledgeApi.put(`/knowledge/api/assistants/${assistantId}/update/`, data)
      return response.data
    },
    onSuccess: (_, { assistantId }) => {
      queryClient.invalidateQueries({ queryKey: ['assistant', assistantId] })
      queryClient.invalidateQueries({ queryKey: ['assistants'] })
    },
  })
}

export function useDeleteAssistant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (assistantId: string) => {
      const response = await knowledgeApi.delete(`/knowledge/api/assistants/${assistantId}/delete/`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants'] })
    },
  })
}

export function useAddKnowledgeBases() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ assistantId, kbIds }: { assistantId: string; kbIds: string[] }) => {
      const response = await knowledgeApi.post(`/knowledge/api/assistants/${assistantId}/knowledge-bases/`, {
        knowledge_base_ids: kbIds,
      })
      return response.data
    },
    onSuccess: (_, { assistantId }) => {
      queryClient.invalidateQueries({ queryKey: ['assistant', assistantId] })
    },
  })
}

export function useRemoveKnowledgeBase() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ assistantId, kbId }: { assistantId: string; kbId: string }) => {
      const response = await knowledgeApi.delete(`/knowledge/api/assistants/${assistantId}/knowledge-bases/${kbId}/`)
      return response.data
    },
    onSuccess: (_, { assistantId }) => {
      queryClient.invalidateQueries({ queryKey: ['assistant', assistantId] })
    },
  })
}

export function useAssistantWidgetSettings(assistantId: string | undefined) {
  return useQuery({
    queryKey: ['assistant-widget-settings', assistantId],
    queryFn: async () => {
      const response = await knowledgeApi.get(`/knowledge/api/assistants/${assistantId}/widget-settings/`)
      return response.data
    },
    enabled: !!assistantId,
  })
}

export function useUpdateWidgetSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ assistantId, data }: { assistantId: string; data: Record<string, any> }) => {
      const response = await knowledgeApi.put(`/knowledge/api/assistants/${assistantId}/widget-settings/update/`, data)
      return response.data
    },
    onSuccess: (_, { assistantId }) => {
      queryClient.invalidateQueries({ queryKey: ['assistant-widget-settings', assistantId] })
    },
  })
}

// ── Import from RAGflow ──────────────────────────────────────────────

export interface DatasetInfo {
  id: string
  name: string
  exists: boolean
}

export interface ImportableAssistant {
  id: string
  name: string
  description: string
  dataset_count: number
  datasets: DatasetInfo[]
  missing_kb_count: number
  created_at: string
}

export function useImportableAssistants(params?: { page?: number; pageSize?: number; search?: string }) {
  const { page = 1, pageSize = 50, search = '' } = params || {}
  return useQuery({
    queryKey: ['importable-assistants', page, pageSize, search],
    queryFn: async () => {
      const response = await knowledgeApi.get('/knowledge/api/assistants/import/available/', {
        params: { page, page_size: pageSize, search },
      })
      return response.data as { items: ImportableAssistant[]; total: number; page: number; page_size: number }
    },
  })
}

export function useImportAssistant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { ragflow_chat_id: string; name?: string }) => {
      const response = await knowledgeApi.post('/knowledge/api/assistants/import/', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistants'] })
      queryClient.invalidateQueries({ queryKey: ['importable-assistants'] })
      queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] })
    },
  })
}

// ── Cleanup Test Assistants ──────────────────────────────────────────

export interface TestAssistantPreview {
  id: string
  name: string
  ragflow_chat_id: string
  created_at: string
  source: 'both' | 'ragflow_only'
}

export function useCleanupTestAssistants() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (mode: 'preview' | 'confirm') => {
      const response = await knowledgeApi.post('/knowledge/api/assistants/cleanup-test/', {
        [mode]: true,
      })
      return response.data
    },
    onSuccess: (_, mode) => {
      if (mode === 'confirm') {
        queryClient.invalidateQueries({ queryKey: ['assistants'] })
      }
    },
  })
}
